import React, { useMemo, useState } from "react";
import { CheckCircle2, Clock, Lock, Search, UserX, Users, X } from "lucide-react";

import { Avatar, Card, EmptyState, PageHeader, Pill, SegmentedBar, StatCard, TabSkeleton } from "./ui";
import { SORTS, STATUS_LABELS, computeGuestStats, filterGuests, normalizeStatus, peopleOf } from "../guestStats";

const STATUS_TONE = { confirmed: "success", pending: "warning", declined: "danger" };
const FILTERS = [
  { id: "all", label: "Tous" },
  { id: "confirmed", label: "Confirmés" },
  { id: "pending", label: "En attente" },
  { id: "declined", label: "Absents" },
];

const plural = (count, one, many) => `${count} ${count > 1 ? many : one}`;

function Breakdown({ title, rows, activeName, onPick }) {
  if (!rows.length) return null;
  return (
    <div>
      <h3 className="mb-3 text-[11px] font-bold uppercase tracking-wider text-[#6a6055]">{title}</h3>
      <ul className="space-y-2.5">
        {rows.map((row) => (
          <li key={row.name}>
            <button
              type="button"
              onClick={() => onPick(activeName === row.name ? "" : row.name)}
              aria-pressed={activeName === row.name}
              className={`w-full rounded-xl px-2 py-1.5 text-left transition-colors hover:bg-[#f6f1e8] ${activeName === row.name ? "bg-[#eef3ec]" : ""}`}
            >
              <div className="mb-1 flex items-baseline justify-between gap-3 text-sm">
                <span className="truncate font-medium text-[#211d19]">{row.name}</span>
                <span className="shrink-0 text-xs text-[#6a6055]">{row.confirmed}/{row.total} confirmés</span>
              </div>
              <SegmentedBar
                height="h-1.5"
                segments={[
                  { value: row.confirmed, tone: "success", label: "Confirmés" },
                  { value: row.pending, tone: "warning", label: "En attente" },
                  { value: row.declined, tone: "danger", label: "Absents" },
                ]}
              />
            </button>
          </li>
        ))}
      </ul>
    </div>
  );
}

/**
 * Follow-up of the guests, read-only: figures, response progress, breakdown by
 * group and side, filters and a list that works from a phone to a desktop.
 * Contact details, RSVP links and notes are never part of this view.
 */
export default function GuestsTab({ guests }) {
  const [query, setQuery] = useState("");
  const [status, setStatus] = useState("all");
  const [group, setGroup] = useState("");
  const [side, setSide] = useState("");
  const [sort, setSort] = useState("status");

  const stats = useMemo(() => computeGuestStats(guests || []), [guests]);
  const visible = useMemo(() => filterGuests(guests || [], { query, status, group, side, sort }), [guests, query, status, group, side, sort]);
  const counts = { all: stats.total, confirmed: stats.confirmed, pending: stats.pending, declined: stats.declined };
  const hasFilter = Boolean(query || group || side || status !== "all");

  if (!guests) return <TabSkeleton />;

  const reset = () => { setQuery(""); setStatus("all"); setGroup(""); setSide(""); };

  return (
    <div className="w-full space-y-6 pb-8">
      <PageHeader
        eyebrow="Suivi des réponses"
        title="Invités"
        subtitle="Vue de suivi en lecture seule : les coordonnées, liens RSVP et notes restent réservés aux mariés."
        actions={<Pill tone="neutral"><Lock className="h-3 w-3" aria-hidden="true" />Lecture seule</Pill>}
      />

      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        <StatCard label="Invitations" value={stats.total} hint={plural(stats.people, "personne au total", "personnes au total")} icon={Users} />
        <StatCard label="Confirmées" value={stats.confirmed} hint={plural(stats.confirmedPeople, "personne attendue", "personnes attendues")} tone="success" icon={CheckCircle2} />
        <StatCard label="En attente" value={stats.pending} hint={stats.pending ? "À relancer" : "Tout le monde a répondu"} tone="warning" icon={Clock} />
        <StatCard label="Absentes" value={stats.declined} hint={plural(stats.declined, "invitation déclinée", "invitations déclinées")} tone="danger" icon={UserX} />
      </div>

      <Card>
        <div className="flex flex-wrap items-baseline justify-between gap-2">
          <h2 className="font-serif text-xl text-[#211d19]">Avancement des réponses</h2>
          <p className="text-sm text-[#5b5247]"><strong className="text-[#211d19]">{stats.responseRate} %</strong> ont répondu ({stats.answered} sur {stats.total})</p>
        </div>
        <div className="mt-4">
          <SegmentedBar
            height="h-3"
            segments={[
              { value: stats.confirmed, tone: "success", label: "Confirmées" },
              { value: stats.pending, tone: "warning", label: "En attente" },
              { value: stats.declined, tone: "danger", label: "Absentes" },
            ]}
          />
        </div>
        <div className="mt-3 flex flex-wrap gap-x-5 gap-y-1 text-xs text-[#5b5247]">
          <span className="flex items-center gap-1.5"><i className="h-2 w-2 rounded-full bg-emerald-500" />Confirmées</span>
          <span className="flex items-center gap-1.5"><i className="h-2 w-2 rounded-full bg-amber-500" />En attente</span>
          <span className="flex items-center gap-1.5"><i className="h-2 w-2 rounded-full bg-rose-500" />Absentes</span>
        </div>
        <div className="mt-6 grid gap-6 border-t border-[#f0e9dc] pt-5 md:grid-cols-2">
          <Breakdown title="Par groupe" rows={stats.groups} activeName={group} onPick={setGroup} />
          <Breakdown title="Par côté" rows={stats.sides} activeName={side} onPick={setSide} />
        </div>
      </Card>

      <Card className="!p-0 overflow-hidden">
        <div className="space-y-3 border-b border-[#f0e9dc] p-4 md:p-5">
          <div className="flex flex-wrap items-center gap-3">
            <label className="relative min-w-[200px] flex-1">
              <span className="sr-only">Rechercher un invité</span>
              <Search className="pointer-events-none absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-[#8b7f70]" aria-hidden="true" />
              <input
                type="search"
                value={query}
                onChange={(event) => setQuery(event.target.value)}
                placeholder="Rechercher un invité ou un groupe"
                className="w-full rounded-xl border border-[#e2d9c8] bg-[#fbf8f2] py-2.5 pl-10 pr-3 text-sm text-[#211d19] outline-none transition-colors placeholder:text-[#8b7f70] focus:border-[#5f7d68] focus:bg-white"
              />
            </label>
            <label className="text-xs font-semibold text-[#5b5247]">
              <span className="sr-only">Trier</span>
              <select value={sort} onChange={(event) => setSort(event.target.value)} className="rounded-xl border border-[#e2d9c8] bg-white px-3 py-2.5 text-sm text-[#211d19] outline-none focus:border-[#5f7d68]">
                {Object.entries(SORTS).map(([key, entry]) => <option key={key} value={key}>{entry.label}</option>)}
              </select>
            </label>
          </div>

          <div className="-mx-1 flex gap-2 overflow-x-auto px-1 pb-1 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden" role="tablist" aria-label="Filtrer par statut">
            {FILTERS.map((filter) => (
              <button
                key={filter.id}
                type="button"
                role="tab"
                aria-selected={status === filter.id}
                onClick={() => setStatus(filter.id)}
                className={`shrink-0 rounded-full border px-3.5 py-1.5 text-xs font-semibold transition-colors ${status === filter.id ? "border-[#211d19] bg-[#211d19] text-white" : "border-[#e2d9c8] bg-white text-[#5b5247] hover:bg-[#f6f1e8]"}`}
              >
                {filter.label} <span className={status === filter.id ? "text-white/70" : "text-[#8b7f70]"}>{counts[filter.id]}</span>
              </button>
            ))}
          </div>

          {hasFilter ? (
            <div className="flex flex-wrap items-center gap-2 text-xs text-[#5b5247]">
              {group ? <Pill tone="info">{group}</Pill> : null}
              {side ? <Pill tone="info">Côté {side}</Pill> : null}
              <button type="button" onClick={reset} className="flex items-center gap-1 font-semibold text-[#5f7d68] hover:underline"><X className="h-3 w-3" aria-hidden="true" />Réinitialiser les filtres</button>
            </div>
          ) : null}
        </div>

        <p className="px-4 pt-3 text-xs text-[#6a6055] md:px-5" aria-live="polite">
          {plural(visible.length, "invitation affichée", "invitations affichées")} sur {stats.total}
        </p>

        {visible.length === 0 ? (
          <div className="p-4 md:p-5">
            <EmptyState icon={Users} title={stats.total ? "Aucun invité ne correspond" : "Aucun invité pour le moment"} text={stats.total ? "Modifiez la recherche ou réinitialisez les filtres." : "Les mariés n'ont pas encore ajouté d'invités."} />
          </div>
        ) : (
          <div role="table" aria-label="Liste des invités" className="mt-2">
            <div role="row" className="hidden grid-cols-[minmax(0,2.2fr)_minmax(0,1.3fr)_minmax(0,1fr)_90px_130px] gap-4 border-y border-[#f0e9dc] bg-[#fbf8f2] px-5 py-2 text-[11px] font-bold uppercase tracking-wider text-[#6a6055] md:grid">
              <span role="columnheader">Invité</span><span role="columnheader">Groupe</span><span role="columnheader">Côté</span><span role="columnheader" className="text-center">Pers.</span><span role="columnheader" className="text-right">Statut</span>
            </div>
            <ul>
              {visible.map((guest) => {
                const state = normalizeStatus(guest.rsvp_status);
                return (
                  <li key={guest.id} role="row" className={`grid grid-cols-[auto_minmax(0,1fr)_auto] items-center gap-x-3 gap-y-1 border-b border-[#f4eee2] px-4 py-3 last:border-b-0 md:grid-cols-[minmax(0,2.2fr)_minmax(0,1.3fr)_minmax(0,1fr)_90px_130px] md:gap-4 md:px-5 ${state === "pending" ? "bg-amber-50/40" : ""}`}>
                    <div role="cell" className="contents md:flex md:min-w-0 md:items-center md:gap-3">
                      <Avatar name={guest.name} seed={guest.group} />
                      <div className="min-w-0 md:flex-1">
                        <p className="truncate font-semibold text-[#211d19]">{guest.name}</p>
                        <p className="truncate text-xs text-[#6a6055] md:hidden">
                          {[guest.group, guest.side ? `Côté ${guest.side}` : "", guest.plus_ones_count ? `+${guest.plus_ones_count}` : ""].filter(Boolean).join(" · ") || "Invité"}
                        </p>
                      </div>
                    </div>
                    <span role="cell" className="hidden truncate text-sm text-[#3f372f] md:block">{guest.group || "—"}</span>
                    <span role="cell" className="hidden truncate text-sm text-[#3f372f] md:block">{guest.side || "—"}</span>
                    <span role="cell" className="hidden text-center text-sm text-[#3f372f] md:block">{peopleOf(guest)}</span>
                    <span role="cell" className="justify-self-end"><Pill tone={STATUS_TONE[state]}>{STATUS_LABELS[state]}</Pill></span>
                  </li>
                );
              })}
            </ul>
          </div>
        )}
      </Card>
    </div>
  );
}
